/**
 * Shared types for the Edit Profile dialog: the RHF form-values shape, and the
 * `updateMyMemberInfo` submit payload it's mapped into.
 */
import type { Gender, PreferredContactMethod } from '@/graphql/generated/graphql';
import type { UsStateCode } from './usStates';

export type { UpdateMyMemberInfoInput } from '@/graphql/generated/graphql';

/**
 * Salesforce's `salutation` is a plain `String`, not an enum — per `update-my-member-info.model.ts`
 * it's also populated by other member-management flows, so its live value set is wider than this
 * form's honorifics. This union is just the 5 options the edit form's dropdown offers.
 */
export type Salutation = 'Mr' | 'Mrs' | 'Miss' | 'Ms' | 'Dr';

const SALUTATIONS: readonly Salutation[] = ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr'];

/**
 * A member whose real `salutation` is outside the dropdown's 5 options seeds the field blank
 * rather than crashing or rendering an option that doesn't exist. Since a blank, untouched field
 * omits on submit (`optionalField`), that out-of-list value is left alone unless the member
 * explicitly picks one of the 5.
 */
export function isSalutation(value: string): value is Salutation {
  return (SALUTATIONS as readonly string[]).includes(value);
}

/**
 * All 19 fields the form manages, as raw strings (a `<select>`/`<input>` never holds anything
 * else). `gender` is required so its empty string ('') is a validation failure, not a valid
 * "unset" state; `preferredContactMethod` is optional, so '' there means "no selection" and maps
 * to omitted on submit. `dateOfBirth` is ISO `yyyy-MM-dd` already — it's a native `<input
 * type="date">` (`FormFloatField` `kind="date"`), which stores/emits ISO regardless of the
 * locale-formatted text the browser displays, so no MM/DD/YYYY parsing is needed on this side.
 */
export interface ProfileFormValues {
  dateOfBirth: string;
  gender: Gender | '';
  email: string;
  phone: string;
  mailingStreet: string;
  mailingCity: string;
  mailingState: UsStateCode | '';
  mailingPostalCode: string;
  salutation: Salutation | '';
  mobilePhone: string;
  otherStreet: string;
  otherCity: string;
  otherState: UsStateCode | '';
  otherPostalCode: string;
  primaryInsurance: string;
  secondaryInsurance: string;
  employer: string;
  jobTitle: string;
  preferredContactMethod: PreferredContactMethod | '';
}

/** One backend validation failure — mirrors the API's `ZodValidationIssue` (`zod-validation.pipe.ts`). */
export interface ProfileValidationIssue {
  /** Dot-joined field path, or the literal `'(root)'` for a cross-field/refine-without-path error. */
  path: string;
  message: string;
}

/**
 * The real `line1`/`line2` pair behind `ProfileFormValues.mailingStreet`/`otherStreet` — each is
 * one flattened display string (a single-line `<input>` can't render two real lines), but
 * `updateMyMemberInfo` always recomputes the whole street from whichever lines it's given. An
 * untouched street field must resend this real pair verbatim (`resolveMailingStreetLines`/
 * `resolveOtherAddress`), or a real `line2` would be silently dropped just because the member
 * edited something unrelated.
 *
 * No equivalent `otherState` entry is needed: `resolveOtherAddress` only ever resends it once the
 * secondary-address group is touched, at which point `profileSchema`'s all-or-nothing check has
 * already guaranteed it's non-blank in the form itself. `mailingState` doesn't need one either — a
 * different reason, it's simply required, so `profileSchema` blocks it from ever being blank.
 */
export interface OriginalAddressLines {
  mailingLine1: string | null;
  mailingLine2: string | null;
  otherLine1: string | null;
  otherLine2: string | null;
}
